
import { TPostcodeValues } from "@/models/postcode";
import { SMQuestionnaire } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { TAnswerBlock } from "../TAnswerBlocks";

const VALUE_OTHER = "other";

const VALUE_COUNTRY = "country";
const VALUE_ADDRESS = "address";
const VALUE_CITY = "city";
const VALUE_COUNTY = "county";
const VALUE_POSTCODE = "postcode";
const VALUE_LINE_1 = "line1";
const VALUE_LINE_2 = "line2";

const VALUES = [
  VALUE_COUNTRY,
  VALUE_ADDRESS,
  VALUE_CITY,
  VALUE_COUNTY,
  VALUE_POSTCODE,
  VALUE_LINE_1,
  VALUE_LINE_2,
];

const POSTCODE_VALUES = {
  [VALUE_ADDRESS]: "",
  [VALUE_CITY]: "",
  [VALUE_COUNTY]: "",
  [VALUE_POSTCODE]: "",
  [VALUE_LINE_1]: "",
  [VALUE_LINE_2]: "",
};

@Component
export default class AnswerBlockPostcode extends Vue {
  @Prop({ type: Object, default: () => ({}) }) readonly answerBlockData!: TAnswerBlock;
  @Prop({ type: Number, default: () => 0 }) readonly index: number | undefined;
  @Prop({ type: Number, default: () => 0 }) readonly loopIndex: number | undefined;

  values = VALUES;

  postcodeValues: TPostcodeValues = POSTCODE_VALUES;

  @Watch("postcode")
  updatePostcode(value: string) {
    this.validationStatus = null;

    if (this.postcodeCheckTimeout) {
      clearTimeout(this.postcodeCheckTimeout);
      this.postcodeCheckTimeout = null;
    }

    if (!value?.trim()) {
      this.updateQuestionnaireAnswer({ [VALUE_POSTCODE]: "" });
      return;
    }

    this.postcodeCheckTimeout = window.setTimeout(() => {
      this.searchAddress();
    }, 1000);
  }

  updateQuestionnaireAnswer(values: Record<string, string>) {
    Object.keys(values).forEach((value) => {
      this.answerBlocks?.forEach((answerBlock) => {
        const conditionValues = answerBlock.condition.split(" || ");

        if (!conditionValues?.length) return;

        if (conditionValues[0] === value) {
          SMQuestionnaire.updateAnswer({
            index: answerBlock.index,
            value: values[value] ?? "",
            required: answerBlock.is_required,
            answer_block_id: answerBlock.ID,
            answer_block_alias: answerBlock.answer_block_alias,
            loop_index: answerBlock.loop_index,
            condition: answerBlock.condition,
          });
        }
      });
    });

    this.$emit("updateComponentKey");
  }

  postcode = "";

  isLoading = false;

  postcodeCheckTimeout: number | null = null;

  validationStatus: "valid" | "invalid" | null = null;

  async searchAddress() {
    if (!this.postcode) return;

    this.isLoading = true;

    const { postcode } = this;

    try {
      await this.axios.get(API_ROUTES.POSTCODE(postcode));

      this.updateQuestionnaireAnswer({ [VALUE_POSTCODE]: postcode });
      this.validationStatus = "valid";
    } catch {
      this.updateQuestionnaireAnswer({ [VALUE_POSTCODE]: "" });
      this.validationStatus = "invalid";
      SMQuestionnaire.setCanGoNext(false);
    } finally {
      this.isLoading = false;
    }
  }

  created() {
    const postcode = SMQuestionnaire.answers.find(
      (answer) => answer.condition === VALUE_POSTCODE && this.loopIndex === answer.loop_index
    )?.value;

    this.postcode = postcode === VALUE_OTHER || !postcode ? "" : postcode;

    this.values.forEach((value) => {
      this.postcodeValues[value] =
        SMQuestionnaire.answers.find((answer) => {
          const conditionValues = answer.condition.split(" || ");

          if (!conditionValues?.length) return "";

          return conditionValues[0] === value;
        })?.value ?? "";
    });

    this.updateQuestionnaireAnswer(this.postcodeValues);
  }

  beforeDestroy() {
    if (this.postcodeCheckTimeout) {
      clearTimeout(this.postcodeCheckTimeout);
      this.postcodeCheckTimeout = null;
    }
  }

  get answerBlocks() {
    return SMQuestionnaire.currentStep?.question.answer_blocks;
  }
}
